// The UI wiring shared by every closing-game variant (Cricket, Tactics,
// Random Cricket, ...): a rounds-grid fed by click-to-mark input and a
// single global Undo button. Only the rules differ per variant (which
// targets, and how they're scored), which is exactly what `gameDefinition`
// (built by games/shared/closing-game.js) supplies - so a variant is just
// this factory called with its own definition.
import { mountRoundsGrid, updateCell, updateTotal, updateCornerContent, updateRowStrike } from '../../components/rounds-grid.js';
import { saveGameToHistory } from '../../core/db.js';

function randomIntFromInterval(min, max) { // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
}

export function createClosingGameController(gameDefinition) {
    let state = {};
    let history = [];
    let currentPlayers = [];
    // Resolved fresh from gameDefinition.getTargets() on every mount(), not
    // read once at module load - that's what lets a variant like Random
    // Cricket hand back a different target list each new game.
    let targets = [];
    let rounds = [];
    let onChangeCallback = null;
    let isObserver = false;

    function undoButtonHTML() {
        const disabled = history.length === 0;
        // Pill-shaped icon+label button, shared by every closing-game
        // variant (Cricket, Tactics, Random Cricket, Cutthroat) - one
        // implementation, not a per-variant special case. Cricket keeps its
        // own accent color via grid-icon-btn-cricket (see styles.css); the
        // others fall back to the default primary color.
        return `<button class="grid-icon-btn-pill grid-icon-btn-${gameDefinition.id}" type="button" ${disabled ? 'disabled' : ''} title="Undo last mark">
            <i class="fa-solid fa-rotate-left"></i> Undo
        </button>`;
    }

    function cellContentFor(player, roundIndex) {
        return gameDefinition.cellContent(state, currentPlayers, player, targets[roundIndex]);
    }

    // ---- Observer (wall-display) board ----------------------------------
    // A chalkboard: the targets run down a centre column, the players are
    // split across its left and right, each with their score in big type on
    // top. An observer never taps anything, so this replaces the rounds
    // grid entirely (see styles.css's .obs-cricket).
    let observerBoardEl = null;

    function escapeHTML(text) {
        return String(text).replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
    }

    function targetLabel(target) {
        return target === 'bull' ? 'B' : String(target);
    }

    // "15-20 + bull" for a run of numbers, otherwise the plain list (Random
    // Cricket's targets aren't consecutive).
    function targetsSummary() {
        const nums = targets.filter(t => t !== 'bull');
        const consecutive = nums.every((n, i) => i === 0 || n === nums[i - 1] - 1);
        const numbers = consecutive ? `${nums[nums.length - 1]}–${nums[0]}` : nums.join(' ');
        return targets.includes('bull') ? `${numbers} + bull` : numbers;
    }

    // The dart the last click added, so its mark can pulse: a mark effect
    // names its player, a score effect names who it credited (unambiguous
    // only when it credited exactly one player).
    function lastHit() {
        const effect = history[history.length - 1];
        if (!effect) return null;
        if (effect.type === 'mark') return { player: effect.player, target: effect.target };
        if (effect.credits.length === 1) return { player: effect.credits[0].recipient, target: effect.target };
        return null;
    }

    function mountObserverBoard(container) {
        container.innerHTML = '<div class="obs-board obs-cricket"></div>';
        observerBoardEl = container.firstElementChild;
        renderObserverBoard();
    }

    function renderObserverBoard() {
        if (!observerBoardEl) return;
        const n = currentPlayers.length;
        // Even counts split evenly around the target column; an odd count
        // would leave one side a player heavier, so the target column moves
        // to the far left instead.
        const leftCount = n % 2 === 0 ? n / 2 : 0;
        const scores = currentPlayers.map(player => gameDefinition.totalScore(state, player, targets));
        const lowestWins = gameDefinition.rankPlayers(['a', 'b'], { a: 0, b: 1 })[0] === 'a';
        const last = lastHit();
        const accentOf = i => `--accent:var(--player-${(i % 5) + 1})`;

        // Left players, the target column (null), then right players.
        const order = [
            ...currentPlayers.slice(0, leftCount).map((_, i) => i),
            null,
            ...currentPlayers.slice(leftCount).map((_, i) => leftCount + i)
        ];

        let html = `<div class="obs-head"><span>${escapeHTML(gameDefinition.name)} <span class="obs-sub">&middot; ${targetsSummary()}${lowestWins ? ' &middot; lowest score wins' : ''}</span></span>`
            + `<span class="obs-legend"><span class="obs-key scoring">Scoring</span><span class="obs-key closed">Closed</span><span class="obs-key shut">Shut</span></span></div>`;
        const playerCols = count => (count > 0 ? `repeat(${count}, minmax(0, 1fr)) ` : '');
        const columns = `${playerCols(leftCount)}calc(var(--obs-mid) * var(--obs-u)) ${playerCols(n - leftCount)}`.trim();
        html += `<div class="obs-chalk" style="grid-template-columns:${columns};--obs-rows:${targets.length}">`;
        order.forEach(i => {
            if (i === null) { html += '<div class="obs-midhead">Target</div>'; return; }
            html += `<div class="obs-phead" style="${accentOf(i)}"><div class="obs-pname">${escapeHTML(currentPlayers[i])}</div><div class="obs-pscore">${scores[i]}</div></div>`;
        });
        targets.forEach((target, roundIndex) => {
            const shut = gameDefinition.isRowDead(state, currentPlayers, target);
            order.forEach(i => {
                if (i === null) {
                    html += `<div class="obs-mid${shut ? ' shut' : ''}">${targetLabel(target)}</div>`;
                    return;
                }
                const player = currentPlayers[i];
                const { html: markHTML, classes } = cellContentFor(player, roundIndex);
                const kind = classes.includes('scorable') ? 'scorable' : classes.includes('dead') ? (shut ? 'shut' : 'closed') : '';
                const pulse = last && last.player === player && last.target === target ? ' last' : '';
                html += `<div class="obs-ccell ${kind}${pulse}">${markHTML}</div>`;
            });
        });
        html += '</div>';

        observerBoardEl.classList.toggle('obs-many', n > 4);
        observerBoardEl.classList.toggle('obs-tall', targets.length > 7);
        observerBoardEl.innerHTML = html;
    }

    function refreshAll() {
        if (isObserver) {
            renderObserverBoard();
            return;
        }
        currentPlayers.forEach(player => {
            targets.forEach((_, roundIndex) => {
                updateCell(player, roundIndex, cellContentFor(player, roundIndex));
            });
            updateTotal(player, gameDefinition.totalScore(state, player, targets));
        });
        // A single hit can close a target for everyone at once (the last
        // player to close it), so this is re-checked for every round on
        // every refresh, not just the round that was actually hit.
        targets.forEach((target, roundIndex) => {
            updateRowStrike(roundIndex, gameDefinition.isRowDead(state, currentPlayers, target));
        });
        updateCornerContent(undoButtonHTML());
    }

    function notifyChange({ suppressComplete = false } = {}) {
        if (!onChangeCallback) return;
        const scores = {};
        currentPlayers.forEach(player => { scores[player] = gameDefinition.totalScore(state, player, targets); });
        const complete = !suppressComplete && gameDefinition.isComplete(state, currentPlayers, targets);
        onChangeCallback({ scores, complete });
    }

    function handleCellClick(player, roundIndex) {
        const target = targets[roundIndex];
        gameDefinition.addHit(state, currentPlayers, player, target, history);
        // A single hit can change what's scorable for every player on this
        // target (closing/reopening it for everyone else), so the whole
        // board is refreshed rather than just the clicked cell.
        refreshAll();
        notifyChange();
    }

    function handleUndo() {
        const undone = gameDefinition.undoLast(state, history);
        if (!undone) return;
        refreshAll();
        // Undo must never be what finishes the game - it's a correction,
        // not a move. With 3+ players it's mathematically possible for
        // reversing an old score to happen to leave a currently-closed
        // player newly satisfying the win condition entirely as a side
        // effect (isComplete only compares the current snapshot, not how it
        // got there) - suppressing completion here means that only ever
        // surfaces once a real subsequent hit re-confirms it.
        notifyChange({ suppressComplete: true });
    }

    function hasProgress() {
        return currentPlayers.some(player =>
            targets.some(target => state[player][target].marks > 0)
        );
    }

    // ?debug=1 simulates a full game so history/stats views can be
    // exercised without clicking through a real one by hand - rather than
    // fabricating a final marks/score state, it replays through the exact
    // same gameDefinition.addHit() every real click uses (in 3-dart turns,
    // alternating players, same as actual play), so marks, scoring, and
    // "dead" targets all come out exactly as real rules would produce them.
    // `missRate` (0-0.2, rolled once per simulated game) is a dart simply
    // missing the board's numbers entirely - the only kind of "wasted" dart
    // that isn't already just hitting an already-closed target for no gain.
    function simulateGame(missRate) {
        const maxDarts = 1000; // real games finish in a small fraction of this
        let darts = 0;
        let playerIndex = 0;
        while (!gameDefinition.isComplete(state, currentPlayers, targets) && darts < maxDarts) {
            for (let dart = 0; dart < 3; dart++) {
                darts++;
                if (gameDefinition.isComplete(state, currentPlayers, targets)) break;
                if (Math.random() < missRate) continue; // this dart missed the board entirely
                const target = targets[randomIntFromInterval(0, targets.length - 1)];
                gameDefinition.addHit(state, currentPlayers, currentPlayers[playerIndex], target, history);
            }
            playerIndex = (playerIndex + 1) % currentPlayers.length;
        }
    }

    function getState() {
        return JSON.parse(JSON.stringify({ state, history }));
    }

    function setState(snapshot) {
        state = snapshot.state;
        history = snapshot.history;
        refreshAll();
        // See x01-game-controller.js's setState for why this matters - a
        // client that only receives a finished match over sync still needs
        // this to reach app.js's handleGameChange and arm the victory modal.
        notifyChange();
    }

    function mount(container, { players, gameId, onChange, observer = false }) {
        currentPlayers = players;
        onChangeCallback = onChange;
        isObserver = observer;
        targets = gameDefinition.getTargets();
        rounds = gameDefinition.buildRounds(targets);
        state = gameDefinition.createState(players, targets);
        history = gameDefinition.createHistory();

        const simulateRandomData = !observer && new URLSearchParams(window.location.search).get('debug');
        if (simulateRandomData) {
            simulateGame(Math.random() * 0.2);
        }

        if (observer) {
            mountObserverBoard(container);
            return;
        }

        mountRoundsGrid(container, {
            rounds,
            players,
            cellContent: cellContentFor,
            onCellClick: handleCellClick,
            cornerContent: undoButtonHTML(),
            onCornerClick: handleUndo
        });

        currentPlayers.forEach(player => updateTotal(player, gameDefinition.totalScore(state, player, targets)));

        if (simulateRandomData) {
            const scores = {};
            currentPlayers.forEach(player => { scores[player] = gameDefinition.totalScore(state, player, targets); });
            saveGameToHistory({ gameId, gameType: gameDefinition.id, players: currentPlayers, scores });
        }
    }

    return { definition: gameDefinition, mount, hasProgress, getState, setState };
}
